import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PhotoCamera as CameraIcon,
  FlipCameraAndroid as FlipIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Refresh as RetakeIcon,
} from '@mui/icons-material';
import Webcam from 'react-webcam';

interface SimpleCameraCaptureProps {
  open: boolean;
  onClose: () => void;
  onCapture: (imageBlob: Blob, imagePreview: string) => void;
  title?: string;
}

export const SimpleCameraCapture: React.FC<SimpleCameraCaptureProps> = ({
  open,
  onClose,
  onCapture,
  title = 'Foto aufnehmen',
}) => {
  const webcamRef = useRef<Webcam>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [videoDimensions, setVideoDimensions] = useState<{ width: number; height: number } | null>(null);
  const JPEG_QUALITY = 0.95;

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setCapturedImage(null);
      setCapturedBlob(null);
      setCameraError(null);
      setIsCameraReady(false);
      setIsProcessing(false);
      setVideoDimensions(null);
    }
  }, [open]);

  // Video constraints for the camera
  const videoConstraints = useMemo(() => ({
    facingMode,
    width: { min: 1280, ideal: 4096 },
    height: { min: 720, ideal: 4096 },
  }), [facingMode]);

  // Capture photo from webcam
  const capture = useCallback(async () => {
    if (!webcamRef.current || !isCameraReady || isProcessing) return;

    setIsProcessing(true);
    
    try {
      const videoElement = webcamRef.current.video as HTMLVideoElement | null;

      if (!videoElement || !videoDimensions) {
        throw new Error('Video stream not ready');
      }

      const { width, height } = videoDimensions;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Canvas context unavailable');
      }

      if (facingMode === 'user') {
        // Mirror front camera to match on-screen preview
        context.translate(width, 0);
        context.scale(-1, 1);
      }

      context.drawImage(videoElement, 0, 0, width, height);

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
      );

      if (!blob) {
        throw new Error('Failed to create image blob');
      }

      const preview = canvas.toDataURL('image/jpeg', JPEG_QUALITY);

      setCapturedImage(preview);
      setCapturedBlob(blob);
    } catch (error) {
      console.error('Capture error:', error);
      setCameraError('Foto konnte nicht aufgenommen werden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsProcessing(false);
    }
  }, [JPEG_QUALITY, facingMode, isCameraReady, isProcessing, videoDimensions]);

  // Confirm and send captured image
  const confirmCapture = () => {
    if (capturedBlob && capturedImage) {
      onCapture(capturedBlob, capturedImage);
      handleClose();
    }
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedImage(null);
    setCapturedBlob(null);
  };

  // Switch between front and back camera
  const switchCamera = () => {
    setIsCameraReady(false);
    setVideoDimensions(null);
    setFacingMode(prev => (prev === 'user' ? 'environment' : 'user'));
  };

  // Handle dialog close
  const handleClose = () => {
    setCapturedImage(null);
    setCapturedBlob(null);
    setCameraError(null);
    onClose();
  };

  // Handle webcam ready
  const handleUserMedia = () => {
    setCameraError(null);

    const video = webcamRef.current?.video as HTMLVideoElement | undefined;
    if (!video) return;

    const updateDimensions = () => {
      if (video.videoWidth && video.videoHeight) {
        setVideoDimensions({ width: video.videoWidth, height: video.videoHeight });
        setIsCameraReady(true);
      }
    };

    if (video.readyState >= 2) {
      updateDimensions();
    } else {
      const metadataHandler = () => {
        updateDimensions();
        video.removeEventListener('loadedmetadata', metadataHandler);
      };
      video.addEventListener('loadedmetadata', metadataHandler);
    }
  };

  // Handle webcam error
  const handleUserMediaError = (error: string | DOMException) => {
    console.error('Webcam error:', error);
    setIsCameraReady(false);
    
    let errorMessage = 'Kamera konnte nicht gestartet werden.';
    
    if (typeof error === 'string') {
      errorMessage = error;
    } else if (error.name === 'NotAllowedError') {
      errorMessage = 'Kamera-Berechtigung verweigert. Bitte erlauben Sie den Kamera-Zugriff in Ihren Browser-Einstellungen.';
    } else if (error.name === 'NotFoundError') {
      errorMessage = 'Keine Kamera gefunden. Stellen Sie sicher, dass eine Kamera angeschlossen ist.';
    } else if (error.name === 'NotReadableError') {
      errorMessage = 'Kamera wird bereits von einer anderen Anwendung verwendet.';
    }
    
    setCameraError(errorMessage);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          m: 1,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Error Alert */}
        {cameraError && (
          <Alert severity="error" sx={{ m: 2 }}>
            {cameraError}
          </Alert>
        )}

        {/* Camera View / Preview */}
        <Box sx={{ position: 'relative', backgroundColor: '#000', minHeight: 400 }}>
          {!capturedImage ? (
            <>
              {/* Live Camera View */}
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                onUserMedia={handleUserMedia}
                onUserMediaError={handleUserMediaError}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
              
              {/* Camera not ready indicator */}
              {!isCameraReady && !cameraError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                  }}
                >
                  <CircularProgress sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: 'white', mt: 2 }}>
                    Kamera wird geladen...
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            /* Preview of captured image */
            <img
              src={capturedImage}
              alt="Captured"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          )}
        </Box>

        {/* Processing indicator */}
        {isProcessing && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Foto wird verarbeitet...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
        {!capturedImage ? (
          <>
            {/* Camera controls */}
            <Button
              startIcon={<FlipIcon />}
              onClick={switchCamera}
              disabled={!isCameraReady || isProcessing}
            >
              Kamera wechseln
            </Button>
            <Button
              variant="contained"
              startIcon={<CameraIcon />}
              onClick={capture}
              disabled={!isCameraReady || isProcessing}
              size="large"
            >
              Foto aufnehmen
            </Button>
          </>
        ) : (
          <>
            {/* Preview controls */}
            <Button
              startIcon={<RetakeIcon />}
              onClick={retakePhoto}
              color="error"
            >
              Wiederholen
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={confirmCapture}
              color="success"
              size="large"
            >
              Verwenden
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
